import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import type { LayerPose } from '@/animation/interpolateClipKeyframes'
import { sortClipsByStart } from '@/animation/timeline/clipConstraints'
import type { AnimationClip, ClipKeyframe } from '@/types/animationClip'
import type { AnimationLayer } from '@/types/animationLayer'
import type { Vec3 } from '@/types/vec3'
import { createCompactRandomId } from '../../../../shared/utils/randomUuid'

export type { LayerPose } from '@/animation/interpolateClipKeyframes'

/** 判定「片段首尾」与相邻片衔接时的时间容差（秒） */
export const CLIP_BOUNDARY_TIME_EPS_SEC = 0.07

/** 新建片段默认时长（秒） */
export const DEFAULT_NEW_CLIP_DURATION_SEC = 5

function opacityFromKeyframeLocal(k: ClipKeyframe): number {
  return clampMaterialOpacity(k.materialOpacity)
}

function createKeyframeId(): string {
  return createCompactRandomId('kf')
}

function cloneVec3(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z }
}

function clonePose(p: LayerPose): LayerPose {
  return {
    position: cloneVec3(p.position),
    rotationDeg: cloneVec3(p.rotationDeg),
    scale: cloneVec3(p.scale),
    materialOpacity: clampMaterialOpacity(p.materialOpacity),
  }
}

function defaultPose(): LayerPose {
  return {
    position: { x: 0, y: 0, z: 0 },
    rotationDeg: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    materialOpacity: 1,
  }
}

/**
 * 将姿态写入关键帧（不含 timeInClip）。
 */
function applyPoseToKeyframe(k: ClipKeyframe, p: LayerPose): void {
  k.position = cloneVec3(p.position)
  k.rotationDeg = cloneVec3(p.rotationDeg)
  k.scale = cloneVec3(p.scale)
}

/**
 * 规范为恰好两个端点关键帧：`t=0` 与 `t=durationSec`。
 *
 * @param clip - 将被原地修改的片段
 */
export function normalizeClipToEndpointPair(clip: AnimationClip): void {
  const d = Math.max(0, clip.durationSec)
  const sorted = [...clip.keyframes].sort((a, b) => a.timeInClip - b.timeInClip)
  let startPose: LayerPose
  let endPose: LayerPose
  if (sorted.length === 0) {
    startPose = defaultPose()
    endPose = clonePose(startPose)
  } else if (sorted.length === 1) {
    const k = sorted[0]!
    const op = opacityFromKeyframeLocal(k)
    startPose = {
      position: cloneVec3(k.position),
      rotationDeg: cloneVec3(k.rotationDeg),
      scale: cloneVec3(k.scale),
      materialOpacity: op,
    }
    endPose = clonePose(startPose)
  } else {
    const a = sorted[0]!
    const b = sorted[sorted.length - 1]!
    startPose = {
      position: cloneVec3(a.position),
      rotationDeg: cloneVec3(a.rotationDeg),
      scale: cloneVec3(a.scale),
      materialOpacity: opacityFromKeyframeLocal(a),
    }
    endPose = {
      position: cloneVec3(b.position),
      rotationDeg: cloneVec3(b.rotationDeg),
      scale: cloneVec3(b.scale),
      materialOpacity: opacityFromKeyframeLocal(b),
    }
  }
  const id0 = sorted[0]?.id ?? createKeyframeId()
  const id1 = sorted.length > 1 ? sorted[sorted.length - 1]!.id : createKeyframeId()
  clip.keyframes = [
    {
      id: id0,
      timeInClip: 0,
      position: cloneVec3(startPose.position),
      rotationDeg: cloneVec3(startPose.rotationDeg),
      scale: cloneVec3(startPose.scale),
      materialOpacity: startPose.materialOpacity,
      screenImageId: sorted[0]?.screenImageId,
    },
    {
      id: id1,
      timeInClip: d,
      position: cloneVec3(endPose.position),
      rotationDeg: cloneVec3(endPose.rotationDeg),
      scale: cloneVec3(endPose.scale),
      materialOpacity: endPose.materialOpacity,
      screenImageId: sorted[sorted.length - 1]?.screenImageId,
    },
  ]
}

/**
 * 相邻且首尾相接的 clip：后一段首帧姿态与前一段尾帧一致。
 *
 * @param layer - 动画图层（原地修改 `clips` 内关键帧）
 */
export function syncAdjacentClipBoundaries(layer: AnimationLayer): void {
  const sorted = sortClipsByStart(layer.clips)
  for (const c of sorted) normalizeClipToEndpointPair(c)
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i]!
    const b = sorted[i + 1]!
    const endA = a.startSec + a.durationSec
    if (Math.abs(endA - b.startSec) > 1e-3) continue
    const last = a.keyframes[a.keyframes.length - 1]
    const first = b.keyframes[0]
    if (!last || !first) continue
    applyPoseToKeyframe(first, {
      position: last.position,
      rotationDeg: last.rotationDeg,
      scale: last.scale,
      materialOpacity: 1,
    })
    first.materialOpacity = opacityFromKeyframeLocal(last)
  }
}

/**
 * 若 `startSec` 紧贴上一段结尾，返回上一段尾关键帧姿态。
 *
 * @param layer - 图层
 * @param startSec - 新片段起点（秒）
 */
export function getPoseFromTouchingPreviousClipEnd(
  layer: AnimationLayer,
  startSec: number,
): LayerPose | null {
  const sorted = sortClipsByStart(layer.clips)
  for (const c of sorted) {
    const end = c.startSec + c.durationSec
    if (Math.abs(end - startSec) > CLIP_BOUNDARY_TIME_EPS_SEC) continue
    normalizeClipToEndpointPair(c)
    const last = c.keyframes[c.keyframes.length - 1]
    if (!last) return null
    return {
      position: cloneVec3(last.position),
      rotationDeg: cloneVec3(last.rotationDeg),
      scale: cloneVec3(last.scale),
      materialOpacity: opacityFromKeyframeLocal(last),
    }
  }
  return null
}

/**
 * 播放头是否落在任一片段的「内部」（不含首尾窄邻域）。
 *
 * @param clips - 该层全部片段
 * @param globalT - 全局时间（秒）
 */
export function isPlayheadInsideClipInterior(clips: AnimationClip[], globalT: number): boolean {
  const t = Number(globalT)
  const eps = CLIP_BOUNDARY_TIME_EPS_SEC
  for (const c of clips) {
    const lo = c.startSec + eps
    const hi = c.startSec + c.durationSec - eps
    if (t > lo && t < hi) return true
  }
  return false
}

/**
 * 将当前图层姿态写入播放头附近的片段端点关键帧（无命中则不修改）。
 *
 * @param layer - 图层
 * @param globalT - 全局时间（秒）
 * @param pose - 要写入的姿态
 */
export function writeLayerPoseToClipBoundariesAtGlobalTime(
  layer: AnimationLayer,
  globalT: number,
  pose: LayerPose,
): void {
  const t = Number(globalT)
  const eps = CLIP_BOUNDARY_TIME_EPS_SEC
  const sorted = sortClipsByStart(layer.clips)

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i]!
    const b = sorted[i + 1]!
    const j = a.startSec + a.durationSec
    if (Math.abs(j - b.startSec) > 1e-3) continue
    if (Math.abs(t - j) < eps) {
      normalizeClipToEndpointPair(a)
      normalizeClipToEndpointPair(b)
      const lastA = a.keyframes[a.keyframes.length - 1]
      const firstB = b.keyframes[0]
      if (lastA && firstB) {
        applyPoseToKeyframe(lastA, pose)
        applyPoseToKeyframe(firstB, pose)
      }
      syncAdjacentClipBoundaries(layer)
      return
    }
  }

  for (const c of sorted) {
    if (Math.abs(t - c.startSec) < eps) {
      normalizeClipToEndpointPair(c)
      const first = c.keyframes[0]
      if (first) applyPoseToKeyframe(first, pose)
      syncAdjacentClipBoundaries(layer)
      return
    }
    const endT = c.startSec + c.durationSec
    if (Math.abs(t - endT) < eps) {
      normalizeClipToEndpointPair(c)
      const last = c.keyframes[c.keyframes.length - 1]
      if (last) applyPoseToKeyframe(last, pose)
      syncAdjacentClipBoundaries(layer)
      return
    }
  }
}

/**
 * 新建片段用的首尾两帧（时间分别为 0 与 `durationSec`）。
 *
 * @param durationSec - 片段时长（秒）
 * @param startPose - 首帧姿态
 * @param endPose - 尾帧姿态
 */
export function createTwoEndpointKeyframes(
  durationSec: number,
  startPose: LayerPose,
  endPose: LayerPose,
): ClipKeyframe[] {
  const d = Math.max(0, Number(durationSec) || 0)
  const s = clonePose(startPose)
  const e = clonePose(endPose)
  return [
    {
      id: createKeyframeId(),
      timeInClip: 0,
      position: cloneVec3(s.position),
      rotationDeg: cloneVec3(s.rotationDeg),
      scale: cloneVec3(s.scale),
      materialOpacity: clampMaterialOpacity(s.materialOpacity),
    },
    {
      id: createKeyframeId(),
      timeInClip: d,
      position: cloneVec3(e.position),
      rotationDeg: cloneVec3(e.rotationDeg),
      scale: cloneVec3(e.scale),
      materialOpacity: clampMaterialOpacity(e.materialOpacity),
    },
  ]
}

/**
 * 将片段两端关键帧设为默认姿态（用于「重置」且播放头落在该片段内时）。
 *
 * @param clip - 片段
 */
export function resetClipEndpointsToDefaultPose(clip: AnimationClip): void {
  const p = defaultPose()
  clip.keyframes = [
    {
      id: createKeyframeId(),
      timeInClip: 0,
      position: cloneVec3(p.position),
      rotationDeg: cloneVec3(p.rotationDeg),
      scale: cloneVec3(p.scale),
      materialOpacity: 1,
    },
    {
      id: createKeyframeId(),
      timeInClip: Math.max(0, clip.durationSec),
      position: cloneVec3(p.position),
      rotationDeg: cloneVec3(p.rotationDeg),
      scale: cloneVec3(p.scale),
      materialOpacity: 1,
    },
  ]
}
